import { useState } from 'react';
import { matchPath, Outlet, useLocation } from 'react-router-dom';
import { MEASUREMENT_REPORT_WINDOW_ROUTE } from '@/features/properties/lib/property-urls';
import { ErrorBoundary } from '@/components/layout/ErrorBoundary';
import { PageViewTracker } from '@/components/layout/PageViewTracker';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sidebar } from './Sidebar';
import { AppHeader } from './AppHeader';
import { PageHeaderSlotsProvider } from './PageHeaderSlots';
import { ImpersonationBanner } from './ImpersonationBanner';
import { SessionTimeoutModal } from './SessionTimeoutModal';
import { OrdersCsvExportBanner } from '@/features/orders/components/OrdersCsvExportBanner';
import { TruSpaceIconBadge } from '@/components/brand/TruSpaceIconBadge';
import { AiChatPanel } from '@/features/ai-assistant/components/AiChatPanel';
import { DashboardScopeProvider } from '@/features/dashboard/components/DashboardScopeContext';
import { RoadmapAgentHost } from '@/features/roadmap/components/RoadmapAgentPanel';
import { isRoadmapWorkspacePath } from '@/features/roadmap/lib/roadmap-workspace';
import { ZendeskMessenger } from '@/features/support/components/ZendeskMessenger';
import { useUIStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { isReleaseFeatureEnabled } from '@/lib/release-flags';
import { GettingStartedChecklist } from '@/features/activation/components/GettingStartedChecklist';
import { FirstRunTourHost } from '@/features/tooltips/FirstRunTourHost';
import { LegalAcceptanceDialog } from '@/features/auth/components/LegalAcceptanceDialog';
import { cn } from '@/lib/cn';

/**
 * The main application shell: sidebar + header + content area.
 * All authenticated routes render inside this layout.
 *
 * `DashboardScopeProvider` wraps the whole shell (DEF-269) so TruInsight —
 * both the dashboard widget and the FAB — share the same SSI lens (including
 * sticky session restore). Property dashboard nests its own provider with a
 * building id when that route mounts.
 */
export function AppShell() {
  const { pathname } = useLocation();
  const isReportWindow =
    matchPath({ path: MEASUREMENT_REPORT_WINDOW_ROUTE, end: true }, pathname) != null;
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const sidePanelOpen = useUIStore((s) => s.sidePanelOpenCount > 0);
  const isImpersonating = useAuthStore((s) => !!s.realUser);
  const user = useAuthStore((s) => s.user);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  // Forced Terms + Privacy gate. Password rotation wins when both are open, so
  // a user with a temp password can still reach the /profile password form.
  // Impersonated sessions are exempt — support must not be blocked by the
  // target's acceptance.
  const legalGateOpen =
    user?.mustAcceptLegal === true &&
    user?.mustChangePassword !== true &&
    !isImpersonating;
  // Parity: when Zendesk Support is on, Zendesk's native launcher bubble owns
  // the bottom-right corner. TruInsight FAB stays parked until product decides
  // whether AI ships in V3.0 alongside Zendesk.
  const zendeskSupport = isReleaseFeatureEnabled('support-zendesk');
  const isSsi =
    user?.role === 'SSIAdministrator' || user?.role === 'SSIEmployee';
  // FR-061 — hide the FAB when this company opted out. SSI keeps it (all-companies).
  const companyAiOn = user?.aiEnabled !== false;
  const showAiFab =
    !zendeskSupport &&
    !sidePanelOpen &&
    !aiChatOpen &&
    !legalGateOpen &&
    (isSsi || companyAiOn);

  // FR-300 external window: the document is the report panel, not the app.
  if (isReportWindow) {
    return (
      <TooltipProvider delayDuration={300}>
        <div className="h-dvh min-h-0 bg-background">
          <PageViewTracker />
          <SessionTimeoutModal />
          {legalGateOpen ? (
            <LegalAcceptanceDialog
              needsTerms={user?.mustAcceptTerms !== false}
              needsPrivacy={user?.mustAcceptPrivacy !== false}
            />
          ) : (
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          )}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <PageHeaderSlotsProvider>
      <TooltipProvider delayDuration={300}>
        <DashboardScopeProvider>
        <div
          className="min-h-screen bg-app-backdrop"
          style={{
            // Reserve space for the fixed impersonation banner so header /
            // content are never covered. Height is measured live (0 when idle).
            paddingTop: 'var(--impersonation-banner-height)',
          }}
        >
          <PageViewTracker />
          {/* Skip to content — visible on Tab focus for keyboard users */}
          <a
            href="#main-content"
            className={cn(
              // The skip link is an action, so it takes the teal action colour
              // rather than the Deep Purple brand chrome it used to borrow
              // (`design-tokens.mdc` §1.5).
              'sr-only z-max focus:not-sr-only focus:fixed focus:left-4 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-foreground focus:shadow-lg',
              // `var(--spacing)*4` is the scale's 1rem step — the same value the
              // `focus:top-4` branch resolves to, expressed through the scale
              // instead of a literal (`design-tokens.mdc` §2).
              isImpersonating
                ? 'focus:top-[calc(var(--impersonation-banner-height)+var(--spacing)*4)]'
                : 'focus:top-4',
            )}
          >
            Skip to main content
          </a>
          <ImpersonationBanner />
          <Sidebar />
          <main
            id="main-content"
            className={cn(
              // Tracks the rail's own `transition-[width]` so the content edge
              // and the rail edge move together.
              'transition-[margin] duration-[var(--duration-base)] ease-[var(--ease-out)]',
              collapsed
                ? 'lg:ml-[var(--sidebar-collapsed-width)]'
                : 'lg:ml-[var(--sidebar-width)]',
            )}
          >
            {/* Single sticky app header — carries the page title slot and
                the bell/user-menu. Replaces the old fixed bell-only strip
                plus each page's own title bar. */}
            <AppHeader />
            {/* While the legal gate is open the route is not rendered at all.
                An overlay alone would still let page queries fire and would
                show TruSpace data behind the modal. */}
            {legalGateOpen ? null : (
              <div className="p-4 sm:p-6">
                <GettingStartedChecklist />
                <ErrorBoundary>
                  <Outlet />
                </ErrorBoundary>
              </div>
            )}
          </main>
          {legalGateOpen ? (
            <LegalAcceptanceDialog
              needsTerms={user?.mustAcceptTerms !== false}
              needsPrivacy={user?.mustAcceptPrivacy !== false}
            />
          ) : null}
          {legalGateOpen ? null : <FirstRunTourHost />}
          <SessionTimeoutModal />
          <OrdersCsvExportBanner />
          {/* Zendesk chat owns the corner except on Tickets, Roadmap, and Sprints. */}
          <ZendeskMessenger suppressed={isRoadmapWorkspacePath(pathname)} />
          <RoadmapAgentHost />

          {/* TruInsight FAB — only when Zendesk Support is OFF (native Support mode). */}
          {aiChatOpen && !legalGateOpen && (isSsi || companyAiOn) ? (
            <AiChatPanel isFloating onClose={() => setAiChatOpen(false)} />
          ) : null}
          {showAiFab ? (
            <button
              onClick={() => setAiChatOpen(true)}
              // Safe-area inset on the bottom so the floating CTA doesn't sit under
              // the iOS home indicator on phones / phablets.
              style={{ bottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
              className="fixed right-5 z-sidebar flex h-13 w-13 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-[background-color,box-shadow,transform] duration-[var(--duration-fast)] hover:bg-primary/90 hover:shadow-xl active:scale-95"
              title="TruInsight AI"
              aria-label="Open TruInsight AI"
            >
              <TruSpaceIconBadge size="fab" plain />
            </button>
          ) : null}
        </div>
        </DashboardScopeProvider>
      </TooltipProvider>
    </PageHeaderSlotsProvider>
  );
}
